import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  RelatedEntitiesResult,
  RelatedEntityAction,
  RelatedEntityDispositionResult,
} from '@/types/related-entity';

const EMPTY: RelatedEntitiesResult = {
  reportId: '',
  groups: [],
  pendingCount: 0,
  researchInFlight: false,
};

export interface UseRelatedEntitiesResult {
  data: RelatedEntitiesResult;
  isLoading: boolean;
  error: Error | null;
  /** Id of the entity currently being acted on, so a row can show its own spinner. */
  decidingId: string | null;
  decide: (
    relatedEntityId: string,
    action: RelatedEntityAction
  ) => Promise<RelatedEntityDispositionResult>;
  surface: (relatedEntityId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Related entities for a report request's resolved subjects.
 *
 * Adjudicated in the triage Identification step, once a candidate has been
 * selected. Pass `null` to disable — the usual gating idiom here — so callers
 * can mount unconditionally and only fetch when the caller is an admin (the
 * endpoints are 403 for everyone else).
 */
export function useRelatedEntities(reportRequestId: string | null): UseRelatedEntitiesResult {
  const [data, setData] = useState<RelatedEntitiesResult>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!reportRequestId) {
      setData(EMPTY);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setData(await vnextApiClient.getRelatedEntities(reportRequestId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportRequestId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const decide = useCallback(
    async (relatedEntityId: string, action: RelatedEntityAction) => {
      if (!reportRequestId) {
        throw new Error('No report');
      }
      setDecidingId(relatedEntityId);
      try {
        const result = await vnextApiClient.setRelatedEntityDisposition(
          reportRequestId,
          relatedEntityId,
          action
        );
        // Refetch rather than patching locally: a decision can supersede or
        // reveal other rows, and "research" flips researchInFlight, which the
        // ready-to-start gate reads.
        await refetch();
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        throw e;
      } finally {
        setDecidingId(null);
      }
    },
    [reportRequestId, refetch]
  );

  const surface = useCallback(
    async (relatedEntityId: string) => {
      if (!reportRequestId) {
        return;
      }
      setDecidingId(relatedEntityId);
      try {
        await vnextApiClient.surfaceRelatedEntity(reportRequestId, relatedEntityId);
        await refetch();
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        throw e;
      } finally {
        setDecidingId(null);
      }
    },
    [reportRequestId, refetch]
  );

  return { data, isLoading, error, decidingId, decide, surface, refetch };
}
